/**
 * VH-83: Recipe detail page.
 *
 * Renders the active version (header, photo, ingredients, steps, equipment),
 * an equipment-mismatch banner when applicable, version-DAG sidebar,
 * fork/edit/delete/translate actions.
 *
 * Edit and fork use a single inline modal that wraps CaptureManualForm.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  ShareIcon,
  TrashIcon,
  GlobeAltIcon,
  PhotoIcon,
  CalendarDaysIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import toast, { Toaster } from 'react-hot-toast'
import {
  useRecipe,
  useRecipeVersions,
  useDeleteRecipe,
  useModifyVersion,
  useForkVersion,
  useSwitchCurrentVersion,
  useDeleteVersion,
  useTranslateRecipe,
} from '../hooks/useRecipes'
import CaptureManualForm from '../components/recipes/CaptureManualForm'
import EquipmentMismatchBanner from '../components/recipes/EquipmentMismatchBanner'
import VersionDAGViewer from '../components/recipes/VersionDAGViewer'
import RecipeCoverUploader from '../components/recipes/RecipeCoverUploader'
import RecipeVideoSection from '../components/recipes/RecipeVideoSection'
import RecipePrintActions from '../components/recipes/RecipePrintActions'
import RecipePrintableView from '../components/recipes/RecipePrintableView'
import RecipeTips from '../components/recipes/RecipeTips'
import RecipeNutritionPanel from '../components/recipes/RecipeNutritionPanel'
import StepIngredientLine from '../components/recipes/StepIngredientLine'
import { buildStepIngredients } from '../utils/recipeStepIngredients'
import ScaleRecipeDialog from '../components/recipes/ScaleRecipeDialog'
import '../components/recipes/print-recipe.css'
import AddToMealPlanDialog from '../components/recipes/AddToMealPlanDialog'
import AddToShoppingListButton from '../components/recipes/AddToShoppingListButton'
import { useRecipeCategories } from '../hooks/useRecipes'
import { contentLanguage } from '../i18n/supported'

function MacroTile({ label, value, suffix = '' }) {
  return (
    <div className="rounded-md bg-gray-50 p-3 text-center">
      <div className="text-xs text-gray-500 uppercase tracking-wide">
        {label}
      </div>
      <div className="text-lg font-semibold text-gray-900">
        {Math.round(value || 0)}
        {suffix && <span className="text-xs ml-0.5 text-gray-500">{suffix}</span>}
      </div>
    </div>
  )
}

export default function RecipeDetail() {
  const { id } = useParams()
  const recipeId = parseInt(id, 10)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const lang = contentLanguage(i18n?.language)

  const { data: recipe, isLoading, error } = useRecipe(recipeId)
  const { data: versionsData } = useRecipeVersions(recipeId)
  const { data: categories = [] } = useRecipeCategories(lang)

  const deleteMutation = useDeleteRecipe()
  const modifyMutation = useModifyVersion(recipeId)
  const forkMutation = useForkVersion(recipeId)
  const switchMutation = useSwitchCurrentVersion(recipeId)
  const deleteVersionMutation = useDeleteVersion(recipeId)
  const translateMutation = useTranslateRecipe(recipeId)

  const [editOpen, setEditOpen] = useState(false)
  const [forkOpen, setForkOpen] = useState(false)
  const [forkLabel, setForkLabel] = useState('')
  const [addToPlanOpen, setAddToPlanOpen] = useState(false)
  // VH-107: AI scaling dialog
  const [scaleOpen, setScaleOpen] = useState(false)
  const [translatedView, setTranslatedView] = useState(null)
  const [translateError, setTranslateError] = useState(null)
  const [translating, setTranslating] = useState(false)

  // VH-103: ref to the off-screen printable view, passed to html2canvas.
  const printableRef = useRef(null)

  // Auto-translate when language changes (similar to other VH features).
  useEffect(() => {
    if (!recipe?.current_version || lang === 'en') {
      setTranslatedView(null)
      return
    }
    let cancelled = false
    setTranslating(true)
    setTranslateError(null)
    translateMutation
      .mutateAsync(lang)
      .then((res) => {
        if (cancelled) return
        setTranslatedView(res)
      })
      .catch((err) => {
        if (cancelled) return
         
        console.warn('[RecipeDetail] translation failed', err)
        setTranslateError(t('recipes.detail.translationFailed'))
      })
      .finally(() => {
        if (!cancelled) setTranslating(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipe?.current_version_id, lang])

  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500">
        {t('recipes.loading')}
      </div>
    )
  }

  if (error || !recipe) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">{t('recipes.errorLoading')}</p>
        <Link to="/recipes" className="btn-secondary mt-2 inline-block">
          {t('recipes.actions.back')}
        </Link>
      </div>
    )
  }

  const version = recipe.current_version
  const compat = version?.equipment_compat
  const stepsByOrder = (version?.steps || []).slice().sort(
    (a, b) => a.step_order - b.step_order
  )
  const translatedSteps = translatedView?.steps
    ? Object.fromEntries(translatedView.steps.map((s) => [s.step_id, s.text]))
    : null
  // VH-105: ingredient names translated by the backend, keyed by ingredient_id.
  const translatedIngredients = translatedView?.ingredients
    ? Object.fromEntries(
        translatedView.ingredients.map((i) => [i.ingredient_id, i.name])
      )
    : null

  // VH-209: which ingredients each step mentions, with their amounts and
  // notes. The printable view computes the same thing from the same helper —
  // screen, print and PDF must never disagree about a quantity.
  const ingredientsByOrder = (version?.ingredients || [])
    .slice()
    .sort((a, b) => a.display_order - b.display_order)
  const stepIngredients = buildStepIngredients(
    stepsByOrder,
    ingredientsByOrder,
    { translatedSteps, translatedIngredients }
  )

  const displayedTitle = translatedView?.title || version?.title
  const displayedDescription =
    translatedView?.description || version?.description
  // VH-106: tips fall through to the English source when no translation.
  // The RecipeTips component handles null/whitespace by rendering nothing.
  const displayedTips = translatedView?.tips || version?.tips

  const handleDelete = async () => {
    if (!window.confirm(t('recipes.deleteConfirm'))) return
    try {
      await deleteMutation.mutateAsync(recipeId)
      toast.success(t('recipes.deleteSuccess'))
      navigate('/recipes')
    } catch (err) {
       
      console.error('[RecipeDetail] delete failed', err)
      toast.error(t('recipes.deleteFailed'))
    }
  }

  const handleEditSubmit = async (payload) => {
    if (!version) return
    try {
      await modifyMutation.mutateAsync({
        versionId: version.id,
        payload: payload.version,
      })
      setEditOpen(false)
      toast.success(t('recipes.capture.savedToast'))
    } catch (err) {
       
      console.error('[RecipeDetail] edit failed', err)
      toast.error(t('recipes.capture.saveFailed'))
    }
  }

  const handleForkSubmit = async (payload) => {
    if (!version) return
    try {
      await forkMutation.mutateAsync({
        from_version_id: version.id,
        branch_label: forkLabel || 'variant',
        version: payload.version,
      })
      setForkOpen(false)
      setForkLabel('')
      toast.success(t('recipes.capture.savedToast'))
    } catch (err) {
       
      console.error('[RecipeDetail] fork failed', err)
      toast.error(t('recipes.capture.saveFailed'))
    }
  }

  const handleSwitch = async (v) => {
    try {
      await switchMutation.mutateAsync(v.id)
      toast.success(t('recipes.versions.switchSuccess'))
    } catch (err) {
       
      console.error('[RecipeDetail] switch failed', err)
    }
  }

  const handleDeleteVersion = async (v) => {
    if (!window.confirm(t('recipes.versions.deleteConfirm'))) return
    try {
      await deleteVersionMutation.mutateAsync(v.id)
      toast.success(t('recipes.versions.deleteSuccess'))
    } catch (err) {
       
      console.error('[RecipeDetail] delete version failed', err)
      // Surface the backend's 409 guard reason (active/last/has-children)
      // when present; fall back to a generic message otherwise.
      toast.error(
        err?.response?.data?.detail || t('recipes.versions.deleteFailed')
      )
    }
  }

  const initialEditValue = version
    ? {
        title: version.title,
        description: version.description || '',
        // VH-106: preserve tips when opening the edit/fork modal, otherwise
        // saving would blank out the recipe's tips.
        tips: version.tips || '',
        photo_url: version.photo_url || '',
        servings: version.servings,
        prep_minutes: version.prep_minutes,
        cook_minutes: version.cook_minutes,
        ingredients: version.ingredients,
        steps: version.steps,
        equipment_keys: version.equipment_keys,
        category_id: recipe.category_id,
      }
    : null

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="recipe-detail-page">
      <Toaster position="top-right" />
      <Link
        to="/recipes"
        className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {t('recipes.actions.back')}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          <EquipmentMismatchBanner compat={compat} />

          <div className="bg-surface-container rounded-lg border border-gray-200 overflow-hidden">
            <div className="aspect-[16/9] bg-gray-100 flex items-center justify-center">
              {/* VH-101: prefer recipe-level cover image; fall back to the
                  legacy version photo for recipes that haven't been migrated. */}
              {recipe.cover_image_url || version?.photo_url ? (
                <img
                  src={recipe.cover_image_url || version.photo_url}
                  alt={displayedTitle}
                  className="w-full h-full object-cover"
                />
              ) : (
                <PhotoIcon className="h-16 w-16 text-gray-300" />
              )}
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900">
                  {displayedTitle}
                </h1>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setAddToPlanOpen(true)}
                    className="btn-primary flex items-center gap-1 text-sm"
                    data-testid="add-to-meal-plan-btn"
                  >
                    <CalendarDaysIcon className="h-4 w-4" />
                    {t('recipes.addToMealPlan.button')}
                  </button>
                  {/* VH-274: with the other things one does with a recipe. VH-260 put it
                      beside the ingredients, a screen and a half down on a phone, and
                      it was not found. */}
                  <AddToShoppingListButton
                    recipeId={recipeId}
                    versionId={version?.id}
                    disabled={!version?.ingredients?.length}
                  />
                  {/* VH-107: AI scaling — hidden if recipe has no current_version
                      (malformed, can never happen in practice — defensive). */}
                  {recipe.current_version_id && (
                    <button
                      type="button"
                      onClick={() => setScaleOpen(true)}
                      className="btn-primary flex items-center gap-1 text-sm"
                      data-testid="scale-recipe-btn"
                    >
                      <SparklesIcon className="h-4 w-4" />
                      {t('recipes.actions.scale')}
                    </button>
                  )}
                  {/* VH-103: Print + Download PDF */}
                  <RecipePrintActions
                    recipe={recipe}
                    printableRef={printableRef}
                  />
                  <button
                    type="button"
                    onClick={() => setEditOpen(true)}
                    className="btn-secondary flex items-center gap-1 text-sm"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    {t('recipes.actions.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForkOpen(true)}
                    className="btn-secondary flex items-center gap-1 text-sm"
                  >
                    <ShareIcon className="h-4 w-4" />
                    {t('recipes.actions.fork')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="btn-secondary text-red-600 flex items-center gap-1 text-sm"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {t('recipes.actions.delete')}
                  </button>
                </div>
              </div>

              {displayedDescription && (
                <p className="text-gray-700">{displayedDescription}</p>
              )}

              {translateError && (
                <p className="text-xs text-amber-700">
                  <GlobeAltIcon className="inline h-4 w-4 mr-1" />
                  {translateError}
                </p>
              )}
              {translating && (
                <p className="text-xs text-gray-500">
                  <GlobeAltIcon className="inline h-4 w-4 mr-1" />
                  {t('recipes.detail.translating')}
                </p>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MacroTile
                  label={t('recipes.detail.kcal')}
                  value={version?.total_kcal_per_serving}
                />
                <MacroTile
                  label={t('recipes.detail.protein')}
                  value={version?.total_protein_g_per_serving}
                  suffix="g"
                />
                <MacroTile
                  label={t('recipes.detail.carbs')}
                  value={version?.total_carbs_g_per_serving}
                  suffix="g"
                />
                <MacroTile
                  label={t('recipes.detail.fat')}
                  value={version?.total_fat_g_per_serving}
                  suffix="g"
                />
              </div>
            </div>
          </div>

          {/* VH-101 / VH-102: recipe-level media management. */}
          <div className="bg-surface-container rounded-lg border border-gray-200 p-5 space-y-5">
            <RecipeCoverUploader recipe={recipe} />
            <RecipeVideoSection recipe={recipe} />
          </div>

          <div className="bg-surface-container rounded-lg border border-gray-200 p-5">
            <h2 className="mb-3 text-lg font-semibold text-gray-900">
              {t('recipes.detail.ingredientsTitle')}
            </h2>
            {version?.ingredients?.length ? (
              <ul className="divide-y divide-gray-100">
                {version.ingredients
                  .slice()
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((ing) => (
                    <li
                      key={ing.id}
                      className="py-2 flex items-center justify-between gap-3"
                    >
                      <div>
                        <span className="text-gray-900">
                          {translatedIngredients?.[ing.id] ||
                            ing.food_name_snapshot}
                        </span>
                        {ing.notes && (
                          <span className="text-xs text-gray-500 ml-2">
                            ({ing.notes})
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700 whitespace-nowrap">
                        {ing.amount} {ing.unit}
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">
                {t('recipes.detail.noIngredients')}
              </p>
            )}
          </div>

          {/* VH-236: give the recipe usable nutrition (match / estimate / type, finished weight). */}
          <RecipeNutritionPanel recipeId={recipeId} versionId={version?.id} servings={version?.servings} />

          <div className="bg-surface-container rounded-lg border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('recipes.detail.stepsTitle')}
            </h2>
            {stepsByOrder.length ? (
              <ol className="space-y-3 list-decimal list-inside">
                {stepsByOrder.map((step) => (
                  <li
                    key={step.id}
                    className="text-gray-800"
                    data-testid={`detail-step-${step.id}`}
                  >
                    {translatedSteps?.[step.id] || step.text}
                    {(step.timer_minutes || step.min_oven_temp_c) && (
                      <span className="ml-2 text-xs text-gray-500">
                        {step.timer_minutes
                          ? `· ${t('recipes.minutesShort', {
                              count: step.timer_minutes,
                            })}`
                          : ''}
                        {step.min_oven_temp_c
                          ? ` · ${step.min_oven_temp_c}°C`
                          : ''}
                      </span>
                    )}
                    <StepIngredientLine items={stepIngredients[step.id]} />
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-gray-500">
                {t('recipes.detail.noSteps')}
              </p>
            )}
          </div>

          {/* VH-106: Tips & Recommendations — hidden entirely when empty. */}
          {(displayedTips || '').trim() && (
            <div
              className="bg-surface-container rounded-lg border border-gray-200 p-5"
              data-testid="recipe-tips-section"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                {t('recipes.detail.tipsTitle')}
              </h2>
              <RecipeTips tips={displayedTips} data-testid="recipe-tips" />
            </div>
          )}

          <div className="bg-surface-container rounded-lg border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('recipes.detail.equipmentTitle')}
            </h2>
            {version?.equipment_keys?.length ? (
              <div className="flex flex-wrap gap-2">
                {version.equipment_keys.map((k) => (
                  <span
                    key={k}
                    className="px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-800"
                  >
                    {t(`recipes.equipment.labels.${k}`, k)}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {t('recipes.detail.noEquipment')}
              </p>
            )}
          </div>
        </div>

        {/* Sidebar — version DAG */}
        <aside className="lg:col-span-1">
          <div className="bg-surface-container rounded-lg border border-gray-200 p-5 sticky top-20">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('recipes.versions.title')}
            </h2>
            <VersionDAGViewer
              versions={versionsData?.versions || []}
              currentVersionId={recipe.current_version_id}
              onSwitch={handleSwitch}
              onDelete={handleDeleteVersion}
              switching={switchMutation.isPending}
              deleting={deleteVersionMutation.isPending}
            />
          </div>
        </aside>
      </div>

      {/* VH-85: Add-to-meal-plan dialog */}
      <AddToMealPlanDialog
        isOpen={addToPlanOpen}
        onClose={() => setAddToPlanOpen(false)}
        recipeId={recipeId}
        versionId={version?.id}
        versionServings={version?.servings || 1}
      />

      {/* VH-107: AI scaling dialog */}
      <ScaleRecipeDialog
        open={scaleOpen}
        onClose={() => setScaleOpen(false)}
        recipe={recipe}
        version={version}
      />

      {/* Edit modal */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        {/* VH-139: the scroll lives on the PANEL, not on this wrapper.
              Headless UI treats a mousedown anywhere outside `Dialog.Panel` as
              a request to close — and when the wrapper is what scrolls, its
              scrollbar IS outside the panel. Dragging it closed the dialog, so
              editing a long recipe "switched to view mode" the moment you
              reached for the scrollbar. Wheel scrolling never showed it, which
              is why it survived: the pointer stays inside the panel.

              Keeping the wrapper unscrollable means the only scrollbar belongs
              to the panel, and dragging it is an inside event. */}
        <div className="fixed inset-0 flex items-start justify-center p-4 overflow-hidden">
          <Dialog.Panel className="w-full max-w-3xl bg-surface-container rounded-lg shadow-xl p-6 mt-8 mb-8 max-h-[90vh] overflow-y-auto">
            <Dialog.Title className="text-lg font-semibold mb-3">
              {t('recipes.actions.edit')}
            </Dialog.Title>
            {initialEditValue && (
              <CaptureManualForm
                initialValue={initialEditValue}
                categories={categories}
                onSubmit={handleEditSubmit}
                submitting={modifyMutation.isPending}
              />
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Fork modal */}
      <Dialog
        open={forkOpen}
        onClose={() => setForkOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        {/* VH-139: the scroll lives on the PANEL, not on this wrapper.
              Headless UI treats a mousedown anywhere outside `Dialog.Panel` as
              a request to close — and when the wrapper is what scrolls, its
              scrollbar IS outside the panel. Dragging it closed the dialog, so
              editing a long recipe "switched to view mode" the moment you
              reached for the scrollbar. Wheel scrolling never showed it, which
              is why it survived: the pointer stays inside the panel.

              Keeping the wrapper unscrollable means the only scrollbar belongs
              to the panel, and dragging it is an inside event. */}
        <div className="fixed inset-0 flex items-start justify-center p-4 overflow-hidden">
          <Dialog.Panel className="w-full max-w-3xl bg-surface-container rounded-lg shadow-xl p-6 mt-8 mb-8 max-h-[90vh] overflow-y-auto">
            <Dialog.Title className="text-lg font-semibold mb-3">
              {t('recipes.actions.fork')}
            </Dialog.Title>
            <div className="mb-3">
              <label className="form-label">
                {t('recipes.versions.forkLabelField')}
              </label>
              <input
                type="text"
                value={forkLabel}
                onChange={(e) => setForkLabel(e.target.value)}
                placeholder={t('recipes.versions.forkLabelPlaceholder')}
                className="form-input w-full"
                data-testid="fork-label"
              />
            </div>
            {initialEditValue && (
              <CaptureManualForm
                initialValue={initialEditValue}
                categories={categories}
                onSubmit={handleForkSubmit}
                submitting={forkMutation.isPending}
                submitLabelKey="recipes.versions.fork"
              />
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* VH-103: off-screen printable view. Rendered via a portal directly
          into <body> so the print stylesheet can hide every other body
          child via `body.printing-recipe > *:not(.recipe-printable-host)`
          — without the portal, ancestor divs would either still occupy
          page-height (visibility: hidden) or hide the printable too
          (display: none cascades). It's also the rasterization source
          for html2canvas during PDF export. */}
      {createPortal(
        <div className="recipe-printable-host" aria-hidden="true">
          <RecipePrintableView
            ref={printableRef}
            recipe={recipe}
            version={version}
            displayedTitle={displayedTitle}
            displayedTips={displayedTips}
            translatedSteps={translatedSteps}
            translatedIngredients={translatedIngredients}
          />
        </div>,
        document.body
      )}
    </div>
  )
}
